'use client';

import React from 'react';

import { Card, Heading } from '@ui';
import { SectionHeader } from '@layout';
import { t as translate } from '@utils/translate';

import { RadiusScaleDemo, ShadowTokensDemo, SpacingScaleDemo } from './FoundationTokenDemos';

export interface FoundationsStripProps {
  sectionID?: string;
  classes?: string;
  /** Render only inner grids (no `<Section>`); for nesting inside System Overview. */
  embedded?: boolean;
  /** Hide elevation card—use when shadows are shown in Surfaces & elevation. */
  omitElevation?: boolean;
  isSpanish?: boolean;
}

const FoundationsStrip: React.FC<FoundationsStripProps> = ({
  sectionID = 'foundations-tokens',
  classes = '',
  embedded = false,
  omitElevation = false,
  isSpanish = false,
}) => {
  const locale = isSpanish ? 'es' : 'en';

  const tStr = (fullKey: string, fallback: string) => {
    const v = translate(fullKey, 'design-system', locale);
    if (typeof v === 'string' && v !== fullKey && v.trim()) return v;
    return fallback;
  };

  const title = tStr('ui_kit_page.foundations_strip.title', 'Spacing, radius & elevation');
  const intro = tStr(
    'ui_kit_page.foundations_strip.intro',
    'Concrete tokens behind layout rhythm—pair with the style guide for the full color and typography reference.'
  );
  const spacingLabel = tStr('ui_kit_page.foundations_strip.spacing', 'Spacing scale');
  const radiusLabel = tStr('ui_kit_page.foundations_strip.radius', 'Corner radius');
  const elevationLabel = tStr('ui_kit_page.foundations_strip.elevation', 'Elevation');

  const grid = (
    <div className={`grid${omitElevation ? ' no-elev' : ''}`}>
      <Card className='surface-1 card' header={<Heading as='h4'>{spacingLabel}</Heading>}>
        <SpacingScaleDemo />
      </Card>

      <Card className='surface-1 card' header={<Heading as='h4'>{radiusLabel}</Heading>}>
        <RadiusScaleDemo />
      </Card>

      {!omitElevation && (
        <Card className='surface-1' header={<Heading as='h4'>{elevationLabel}</Heading>}>
          <ShadowTokensDemo />
        </Card>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div
        {...(sectionID ? { id: sectionID } : {})}
        className={`foundations-strip embedded ${classes}`.trim()}
      >
        {grid}
      </div>
    );
  }

  return <SectionHeader title={title} description={intro} as='h3' />;
};

export default FoundationsStrip;
